import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FileDown, Package2, RefreshCw, Scale, Search } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Switch } from '@/shared/components/ui/switch';
import { cn } from '@/shared/lib/cn';
import { downloadWorkbook, fechaArchivoHoy } from '@/shared/lib/xlsx';
import { listInventario, type InventarioRow } from './api';
import { buildDatosValorizado } from './lib/exportValorizado';
import { buildInventarioWorkbook } from './lib/inventarioWorkbook';
import { useActiveCountry } from '@/shared/countries/ActiveCountryProvider';
import { useIsSuperAdmin } from '@/shared/auth/SessionProvider';
import { useRealtimeInvalidate } from '@/shared/realtime/useRealtimeInvalidate';
import {
  DEFAULT_INVENTARIO_FILTER,
  FiltrarPopover,
  inventarioFilterActive,
  type InventarioFilterValue,
} from './components/FiltrarPopover';
import { InventarioTable } from './components/InventarioTable';
import { DetalleInventarioDialog } from './components/DetalleInventarioDialog';
import { ConciliacionDialog, useConciliacion } from './components/ConciliacionDialog';

/**
 * Inventario main page. Mirrors the PowerApps Screen_Inventario layout:
 *
 *   ┌────────────────────────────────────────────────────────────────────┐
 *   │ Inventario  [Todas Unidades ⚫] [Excel] [↻] [Recepciones]  🔎 Filt │
 *   ├────────────────────────────────────────────────────────────────────┤
 *   │ Tipo │ Modelo │ Prest/SR │ Disp │ PagoPend │ PagoParc │ … │ Stock  │
 *   │ ...                                                                │
 *   ├────────────────────────────────────────────────────────────────────┤
 *   │ Hornos: N   ·   Accesorios: M   ·   Repuestos: K                   │
 *   └────────────────────────────────────────────────────────────────────┘
 */
export function InventarioPage() {
  const qc = useQueryClient();
  const { config: activeCountry } = useActiveCountry();
  const [search, setSearch] = useState('');
  const [showEmpty, setShowEmpty] = useState(false);
  const [filter, setFilter] = useState<InventarioFilterValue>(DEFAULT_INVENTARIO_FILTER);
  const [viewing, setViewing] = useState<InventarioRow | null>(null);
  const [verDescuadres, setVerDescuadres] = useState(false);
  // Descuadres (buckets de ventas vs. ventas abiertas): sólo admin, que es
  // quien puede corregirlos desde Editar.
  const esAdmin = useIsSuperAdmin();
  const conciliacionQ = useConciliacion(esAdmin);
  const cantDescuadres = conciliacionQ.data?.rows.length ?? 0;

  const inventarioQ = useQuery({
    // Country in the key — inventario is country-scoped server-side.
    queryKey: ['inventario', 'list', activeCountry.code, showEmpty] as const,
    queryFn: () => listInventario({ showEmpty }),
    staleTime: 60_000,
  });

  // Buckets shift constantly via venta lifecycle hooks + admin edits;
  // any inventory row change refreshes the gallery instantly.
  useRealtimeInvalidate('inventory', ['inventario']);

  const allRows = inventarioQ.data?.rows ?? [];

  // All filtering client-side — the dataset is bounded (one row per
  // active artículo) and fits in memory even for large tenants.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allRows.filter((r) => {
      if (filter.lowStockOnly && r.buckets.disponible > 0) return false;
      if (filter.conPorIngresar && r.buckets.porIngresar <= 0) return false;
      if (filter.tipos.size > 0) {
        const tipo = (r.fields.TipoInventario_I ?? '').trim();
        if (!filter.tipos.has(tipo as never)) return false;
      }
      if (!q) return true;
      return (
        (r.fields.Articulo_I ?? '').toLowerCase().includes(q) ||
        (r.fields.TipoInventario_I ?? '').toLowerCase().includes(q) ||
        r.modelo.toLowerCase().includes(q)
      );
    });
  }, [allRows, filter, search]);

  const filterIsActive = inventarioFilterActive(filter);
  const searchActive = Boolean(search.trim()) || filterIsActive;

  return (
    <div className="flex h-full flex-col gap-4">
      {/* Toolbar — DOM order matches desktop (Title, Toggle, Excel,
          Refresh, Search, Filter). On mobile we re-arrange via CSS
          `order` + a `basis-full` break so the title row gets
          [Title · Toggle · Refresh · Excel(icon)] and search + filter
          drop onto a second row. (Recepciones used to live here as a
          shortcut button — it's now a top-level nav item.) */}
      <div className="flex shrink-0 flex-wrap items-center gap-3">
        <h1 className="mr-auto text-2xl font-semibold tracking-tight text-foreground md:text-3xl">
          Inventario
        </h1>
        {/* Toolbar toggle for desktop only — on mobile this control
            lives inside the Filtrar popover so the title row can host
            Refresh + Excel without overflowing. */}
        <label className="hidden items-center gap-2 text-sm sm:flex">
          <span className="text-muted-foreground">Todas las Unidades</span>
          <Switch
            checked={showEmpty}
            onCheckedChange={setShowEmpty}
            aria-label="Mostrar artículos sin stock"
          />
        </label>
        <Button
          variant="default"
          onClick={() =>
            void exportInventarioToXlsx(filtered, {
              pais: activeCountry.name,
              moneda: activeCountry.currency.primary,
              monedaNombre:
                activeCountry.currency.primary === 'MXN'
                  ? 'pesos mexicanos'
                  : 'pesos argentinos',
              vistaFiltrada: searchActive,
            })
          }
          disabled={filtered.length === 0}
          className="order-4 h-12 w-12 shrink-0 gap-0 p-0 sm:order-none sm:w-auto sm:gap-2 sm:px-4"
          aria-label="Exportar a Excel"
          title="Exportar la vista actual a Excel"
        >
          <FileDown className="h-4 w-4" aria-hidden />
          <span className="hidden sm:inline">Excel</span>
        </Button>
        <Button
          variant="outline"
          size="icon"
          className="order-2 h-12 w-12 shrink-0 sm:order-none"
          onClick={() => qc.invalidateQueries({ queryKey: ['inventario'] })}
          aria-label="Actualizar"
          title="Actualizar"
        >
          <RefreshCw
            className={cn('h-4 w-4', inventarioQ.isFetching && 'animate-spin')}
            aria-hidden
          />
        </Button>
        {esAdmin && (
          <Button
            variant="outline"
            onClick={() => setVerDescuadres(true)}
            className={cn(
              'order-3 h-12 shrink-0 gap-2 px-3 sm:order-none sm:px-4',
              cantDescuadres > 0 && 'border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100',
            )}
            aria-label={`Descuadres de inventario: ${cantDescuadres}`}
            title="Buckets de ventas que no coinciden con las ventas abiertas"
          >
            <Scale className="h-4 w-4" aria-hidden />
            <span className="hidden sm:inline">Descuadres</span>
            {conciliacionQ.data && (
              <span
                className={cn(
                  'inline-flex min-w-6 justify-center rounded-md px-1.5 text-xs font-semibold tabular-nums',
                  cantDescuadres > 0 ? 'bg-amber-200' : 'bg-secondary text-muted-foreground',
                )}
              >
                {cantDescuadres}
              </span>
            )}
          </Button>
        )}
        {/* Forces search + filter onto a new row on mobile only. */}
        <div className="order-5 basis-full sm:hidden" aria-hidden />
        <div className="relative order-6 min-w-[10rem] flex-1 sm:order-none sm:w-56 sm:flex-none">
          <Search
            className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <Input
            variant="filled"
            placeholder="Búsqueda"
            className="pl-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Buscar artículo"
          />
        </div>
        <div className="order-7 sm:order-none">
          <FiltrarPopover
            value={filter}
            onChange={setFilter}
            onReset={() => setFilter(DEFAULT_INVENTARIO_FILTER)}
            showEmpty={showEmpty}
            onShowEmptyChange={setShowEmpty}
          />
        </div>
      </div>

      <InventarioTable
        rows={filtered}
        loading={inventarioQ.isPending}
        error={inventarioQ.isError}
        searchActive={searchActive}
        onView={(row) => setViewing(row)}
        onRetry={() => inventarioQ.refetch()}
      />

      <FooterSummary rows={filtered} totalCount={allRows.length} />

      {verDescuadres && (
        <ConciliacionDialog
          onSelect={(row) => {
            setVerDescuadres(false);
            setViewing(row);
          }}
          onClose={() => setVerDescuadres(false)}
        />
      )}

      {viewing && (
        <DetalleInventarioDialog
          summary={viewing}
          onClose={() => setViewing(null)}
        />
      )}
    </div>
  );
}

/**
 * Footer aggregates Stock total per `TipoInventario_I` group. Matches
 * PowerApps's "Hornos / Accesorios / Repuestos" counters — except
 * we render whatever types actually exist in the filtered result so
 * admins see the real taxonomy, not a hardcoded trio.
 */
function FooterSummary({
  rows,
  totalCount,
}: {
  rows: InventarioRow[];
  totalCount: number;
}) {
  const byTipo = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of rows) {
      const tipo = (r.fields.TipoInventario_I ?? '').trim() || 'Sin Tipo';
      m.set(tipo, (m.get(tipo) ?? 0) + r.totalStock);
    }
    return [...m.entries()].sort((a, b) => b[1] - a[1]);
  }, [rows]);

  return (
    <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 rounded-2xl border bg-card px-3 py-2 text-sm sm:gap-3 sm:px-4 sm:py-3">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 sm:gap-4">
        {byTipo.length === 0 ? (
          <span className="flex items-center gap-2 text-muted-foreground">
            <Package2 className="h-4 w-4" aria-hidden />
            Sin unidades en la vista
          </span>
        ) : (
          byTipo.map(([tipo, total]) => (
            <SummaryPill key={tipo} label={tipo} value={total} />
          ))
        )}
      </div>
      <div className="text-xs text-muted-foreground sm:text-sm">
        {rows.length} / {totalCount} artículo{rows.length === 1 ? '' : 's'}
      </div>
    </div>
  );
}

function SummaryPill({ label, value }: { label: string; value: number }) {
  return (
    <span className="flex items-center gap-1.5 sm:gap-2">
      <span className="text-[11px] uppercase tracking-wide text-muted-foreground sm:text-xs">{label}</span>
      <span className="inline-flex min-w-8 justify-center rounded-md bg-secondary px-1.5 py-0.5 text-[12px] font-semibold tabular-nums text-foreground sm:min-w-10 sm:px-2 sm:text-[13px]">
        {value}
      </span>
    </span>
  );
}

/**
 * Baja el "Inventario valorizado" de la vista actual: dos hojas (Resumen +
 * detalle por artículo) con el formato que definió el cliente el
 * 2026-09-04. La aritmética vive en `buildDatosValorizado` y el layout en
 * `buildInventarioWorkbook`; acá sólo se mapea la fila de la API.
 */
async function exportInventarioToXlsx(
  rows: InventarioRow[],
  ctx: { pais: string; moneda: string; monedaNombre: string; vistaFiltrada: boolean },
): Promise<void> {
  const datos = buildDatosValorizado(
    rows.map((r) => ({
        // Mismo fallback que la tabla (`TipoBadge`): `TipoInventario_I` sólo
        // se estampa al crear la fila desde una recepción, así que las filas
        // sembradas por catálogo lo tienen vacío — 73 de las 87 filas con
        // stock de MX al 2026-09-04. Sin el fallback la columna Tipo salía
        // en blanco justo para el país que pidió este export.
        tipo: r.fields.TipoInventario_I || r.tipoProducto || '',
        modelo: r.modelo || '',
        articulo: r.fields.Articulo_I ?? '',
        precioVenta: r.precioVenta,
        buckets: r.buckets,
        totalStock: r.totalStock,
      })),
    );

  const hoy = new Date();
  const wb = buildInventarioWorkbook(datos, {
    pais: ctx.pais,
    moneda: ctx.moneda,
    monedaNombre: ctx.monedaNombre,
    fecha: hoy.toLocaleDateString('es-AR'),
    vistaFiltrada: ctx.vistaFiltrada,
  });
  await downloadWorkbook(wb, `inventario_valorizado_${fechaArchivoHoy()}.xlsx`);
}
